/**
 * Route definitions for lykbl apps
 * 
 * This file defines all routes for both apps to ensure consistency
 * and enable cross-linking between domains.
 */

// Marketing app routes (lykbl.com)
export const MARKETING_ROUTES = {
  // Public pages
  HOME: '/',
  COLAB: '/colab',
  CAMPAIGN_CONNECT: '/campaign-connect',
  CREATORS: '/creators',
  BRANDS: '/brands',
  PRICING: '/pricing',
  BLOG: '/blog',
  BLOG_POST: '/blog/:slug',
  HELP: '/help',
  ABOUT: '/about',
  PRESS_KIT: '/press-kit',
  
  // Legal
  TERMS: '/legal/terms',
  PRIVACY: '/privacy',
  
  // Auth
  AUTH: '/auth',
  AUTH_CALLBACK: '/auth/callback',
  AUTH_SIGNIN: '/auth/signin',
  
  // Future app routes (lykbl.com/app/*)
  APP: '/app',
  APP_COLAB: '/app/colab',
  APP_CAMPAIGNS: '/app/campaigns',
} as const;

// Ai-AMP routes (lykbl.ai / app.lykbl.ai)
export const AI_AMP_ROUTES = {
  // Public marketplace
  HOME: '/',
  AGENT: '/agent/:slug',
  CATEGORIES: '/categories/:slug',
  BUILD: '/build',
  DOCS: '/docs',
  PRICING: '/pricing',
  
  // Auth
  AUTH: '/auth',
  AUTH_CALLBACK: '/auth/callback',
  
  // App routes (authenticated)
  APP: '/app',
  APP_DASHBOARD: '/app',
  APP_AGENTS: '/app/agents',
  APP_AGENT_DETAIL: '/app/agents/:id',
  APP_RUNS: '/app/runs',
  APP_RUN_DETAIL: '/app/runs/:id',
  APP_INTEGRATIONS: '/app/integrations',
  
  // Settings
  APP_SETTINGS_PROFILE: '/app/settings/profile',
  APP_SETTINGS_BILLING: '/app/settings/billing',
  APP_SETTINGS_ORG: '/app/settings/org',
  
  // Developer routes
  DEV_PORTAL: '/developers',
  DEV_AGENTS: '/developers/agents',
  DEV_AGENT_NEW: '/developers/agents/new',
  DEV_AGENT_EDIT: '/developers/agents/:id/edit',
  DEV_EARNINGS: '/developers/earnings',
  DEV_ANALYTICS: '/developers/analytics',
} as const;

import { DOMAINS } from './constants';

/**
 * Helper to detect environment from hostname
 */
const getEnvFromHostname = () => {
  if (typeof window === 'undefined') return 'PRODUCTION';
  const hostname = window.location.hostname;
  
  let env: 'PRODUCTION' | 'STAGING' | 'PREVIEW' = 'PRODUCTION';
  if (hostname.includes('staging.')) {
    env = 'STAGING';
  } else if (hostname.includes('beta.') || hostname.includes('preview.')) {
    env = 'PREVIEW';
  }
  
  if (hostname !== 'localhost' && !hostname.includes('127.0.0.1')) {
    console.log(`[lykbl] Detected environment "${env}" from hostname: ${hostname}`);
  }
  
  return env;
};

/**
 * Generate a full URL for cross-domain linking
 */
export function getMarketingUrl(path: string, production = true): string {
  if (!production) return `http://localhost:5173${path}`;
  
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  
  // If we're already on a marketing subdomain (like staging-lykbl-marketing.pages.dev)
  // or a preview domain, and the link is for marketing, we should stay on this hostname
  // to avoid breaking the preview session.
  if (hostname.includes('marketing') && (hostname.endsWith('.pages.dev') || hostname.includes('localhost'))) {
    return path || '/';
  }

  // Cloudflare Pages preview deploys use random hostnames that cannot be mapped across projects.
  // Always prefer the stable canonical domains for cross-domain links in this case.
  if (hostname.endsWith('.pages.dev')) {
    return `https://${DOMAINS.MARKETING.PRODUCTION}${path}`;
  }

  const env = getEnvFromHostname();
  const domain = DOMAINS.MARKETING[env];
  return `https://${domain}${path}`;
}

export function getAiAmpUrl(path: string, production = true): string {
  if (!production) return `http://localhost:5174${path}`;
  
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // If we're already on an ai-amp subdomain (like staging-lykbl-ai-amp.pages.dev)
  // or a preview domain, and the link is for ai-amp, we should stay on this hostname.
  if (hostname.includes('ai-amp') && (hostname.endsWith('.pages.dev') || hostname.includes('localhost'))) {
    return path || '/';
  }

  // Cloudflare Pages preview deploys use random hostnames that cannot be mapped across projects.
  // Always prefer the stable canonical domains for cross-domain links in this case.
  if (hostname.endsWith('.pages.dev')) {
    return `https://${DOMAINS.AI_AMP.PRODUCTION}${path}`;
  }

  const env = getEnvFromHostname();
  const domain = DOMAINS.AI_AMP[env];
  return `https://${domain}${path}`;
}

export function getAiAmpAppUrl(path: string, production = true): string {
  if (!production) return `http://localhost:5174${path}`;
  
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  // Cloudflare Pages preview deploys use random hostnames that cannot be mapped across projects.
  // Always prefer the stable canonical domains for cross-domain links in this case.
  if (hostname.endsWith('.pages.dev')) {
    return `https://${DOMAINS.AI_AMP.APP}${path}`;
  }

  const env = getEnvFromHostname();
  // app.lykbl.ai is used for production, for others we use the environment-specific domain
  const domain = env === 'PRODUCTION' ? DOMAINS.AI_AMP.APP : DOMAINS.AI_AMP[env];
  return `https://${domain}${path}`;
}

/**
 * Helper to replace route params
 */
export function buildRoute(route: string, params: Record<string, string>): string {
  let result = route;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, value);
  }
  return result;
}

